import { useEffect, useState } from "react";

import { api } from "@/shared/api";

import { HOST_ORIGIN, isHostImage } from "../../../model/hostNodes";

/** 图片代理(A.53,api 从老分支搬到 `/api/v1/website/image-proxy`,TENANT_STAFF 可用,SSRF 拦截在 api)。 */
const IMAGE_PROXY_PATH = "/website/image-proxy";

/** 直连地址:相对路径补宿主根。 */
function directSrc(value: string): string {
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `${HOST_ORIGIN}${v.startsWith("/") ? "" : "/"}${v}`;
}

/**
 * 当前图预览(从老分支搬)。宿主图直连;**外部图床走服务端代取**再转 blob —— 模板默认图大多在原企业图床上,
 * 带防盗链,浏览器直连 403(图看不见、尺寸也读不出)。带鉴权由前端来取,代理端点不用公开。
 */
export function ProxiedImage({
  value,
  alt = "当前图",
  className,
  onSize,
}: {
  value: string;
  alt?: string;
  className?: string;
  onSize?: (w: number, h: number) => void;
}) {
  const raw = (value ?? "").trim();
  const remote = raw !== "" && !isHostImage(raw);
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!raw) {
      setSrc("");
      setFailed(false);
      return;
    }
    if (!remote) {
      setSrc(directSrc(raw));
      setFailed(false);
      return;
    }
    let cancelled = false;
    let objUrl = "";
    setSrc("");
    setFailed(false);
    void (async () => {
      try {
        const res = await api.getBlob(`${IMAGE_PROXY_PATH}?url=${encodeURIComponent(raw)}`);
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setSrc(objUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [raw, remote]);

  if (!raw) return null;
  if (failed) return <span className="text-2xs text-muted-foreground">取图失败</span>;
  if (!src) return <span className="text-2xs text-muted-foreground">加载中…</span>;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onLoad={(e) => {
        const el = e.currentTarget;
        if (el.naturalWidth > 0 && el.naturalHeight > 0)
          onSize?.(el.naturalWidth, el.naturalHeight);
      }}
      onError={() => setFailed(true)}
    />
  );
}
